import { FC } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { Colors, SystemColors } from 'melp-design/style';
import { useTranslation } from 'react-i18next';
import { parsedQuestionResults } from 'containers/surveys';
import {
  surveyChoiceAnswerTypes,
  surveyRatingScaleAnswerTypes,
} from 'store/surveys';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    lineHeight: 1.4,
    padding: 30,
  },
  fontSM: {
    fontSize: 10,
  },
  fontMD: {
    fontSize: 12,
  },
  fontXL: {
    fontSize: 18,
  },
  fontBold: {
    fontStyle: 'bold',
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: 'row',
  },
  justifyBetween: {
    justifyContent: 'space-between',
  },
  p5: {
    padding: 5,
  },
  mb5: {
    marginBottom: 5,
  },
  mb10: {
    marginBottom: 10,
  },
  mb20: {
    marginBottom: 20,
  },
  textRight: {
    textAlign: 'right',
  },
  bgGrey: {
    backgroundColor: SystemColors.grey[98],
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: SystemColors.black,
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: Colors.elementsGrey,
    paddingVertical: 6,
  },
  resultsColumn: {
    width: 110,
    paddingLeft: 8,
  },
  width200: {
    width: 200,
  },
  width300: {
    width: 200,
  },
});

interface Props {
  title: string;
  settings: Array<{ label: string; value: string }>;
  statistics: Array<{ label: string; value: string | number }>;
  intro?: {
    title: string;
    description?: string;
    imageUrl?: string | null;
  } | null;
  questions: Array<
    ReturnType<typeof parsedQuestionResults>[number] & {
      isRequired?: boolean;
    }
  >;
}

export const SurveyResultsDocument: FC<Props> = ({
  title,
  settings,
  statistics,
  intro,
  questions,
}) => {
  const { t } = useTranslation();

  return (
    <Document>
      <Page size="A4" style={[styles.page, styles.fontSM]}>
        <View style={[styles.mb20]}>
          <Text style={[styles.fontXL]}>{title}</Text>
        </View>

        <View style={[styles.mb10, styles.borderBottom]}>
          <Text style={[styles.fontMD]}>
            {t('sidebar.administratorsInformation')}
          </Text>
        </View>

        <View style={[styles.mb20]}>
          {settings.map((setting, i) => (
            <View style={[styles.mb10]} key={i}>
              <Text style={[styles.fontBold]}>{setting.label}:</Text>
              <Text>{setting.value}</Text>
            </View>
          ))}
        </View>

        <View style={[styles.mb20]}>
          {statistics.map((stat, i) => (
            <View
              style={[
                styles.flexRow,
                styles.justifyBetween,
                styles.bgGrey,
                styles.p5,
                styles.mb5,
              ]}
              key={i}
            >
              <Text>{stat.label}</Text>
              <Text>{stat.value}</Text>
            </View>
          ))}
        </View>

        <View style={[styles.mb10, styles.borderBottom]}>
          <Text style={[styles.fontMD]}>
            {t('benefitTemplates.templateContent')}
          </Text>
        </View>

        {intro ? (
          <View style={[styles.mb20]}>
            <Text style={[styles.fontBold, styles.mb5]}>
              {intro.title || '-'}
            </Text>
            {intro.description?.trim() ? (
              <Text style={[styles.mb5]}>{intro.description}</Text>
            ) : null}
            {intro.imageUrl ? (
              <Image
                src={intro.imageUrl}
                style={[styles.width300, styles.mb5]}
              />
            ) : null}
          </View>
        ) : null}

        {questions.map((question, i) => {
          const isInformationPage = question.type === 'information_page';
          const showsAnswerResults =
            surveyChoiceAnswerTypes.some((type) => type === question.type) ||
            surveyRatingScaleAnswerTypes.some((type) => type === question.type);

          return (
            <View style={[styles.mb20]} key={i}>
              <Text style={[styles.fontBold]}>{question.title}</Text>
              <Text>{t(`surveys.answerType-${question.type}`)}</Text>
              {typeof question.isRequired === 'boolean' &&
              !isInformationPage ? (
                <Text>
                  {`${t('surveys.question_required')}: ${
                    question.isRequired ? t('common.yes') : t('common.no')
                  }`}
                </Text>
              ) : null}
              {question.imageUrl ? (
                <Image
                  src={question.imageUrl}
                  style={[styles.width300, styles.mb5]}
                />
              ) : null}
              {showsAnswerResults ? (
                <View style={[styles.mb5]}>
                  <View style={[styles.answerRow]}>
                    <View style={[styles.flex1]} />
                    <Text style={[styles.resultsColumn, styles.textRight]}>
                      {t('surveys.respondentsCount')}
                    </Text>
                    <Text style={[styles.resultsColumn, styles.textRight]}>
                      {t('surveys.respondentsPercentage')}
                    </Text>
                  </View>
                  {question.answers.map((answer, answerIndex) => (
                    <View style={[styles.answerRow]} key={answerIndex}>
                      <View style={[styles.flex1]}>
                        <Text>{`${answer.isCorrect ? '✔' : ''} ${
                          answer.label
                        }`}</Text>
                        {answer.imageUrl ? (
                          <Image
                            src={answer.imageUrl}
                            style={[styles.width200]}
                          />
                        ) : null}
                      </View>
                      <Text style={[styles.resultsColumn, styles.textRight]}>
                        {answer.responseCount}
                      </Text>
                      <Text style={[styles.resultsColumn, styles.textRight]}>
                        {answer.responsePercents}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          );
        })}
      </Page>
    </Document>
  );
};
